import { memo } from "react";
import { Gauge, Pause, Play, Repeat, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import ChordTimelineStrip from "@/components/music/ChordTimelineStrip";

const formatTime = (seconds = 0) => {
  const safe = Math.max(0, Number(seconds) || 0);
  return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, "0")}`;
};

const PracticeSongPlayer = memo(
  ({
    currentTime,
    duration,
    isPlaying,
    progress,
    chords,
    currentIndex,
    loopEnabled,
    playbackRate,
    volume,
    onTogglePlay,
    onSeek,
    onToggleLoop,
    onPlaybackRateChange,
    onVolumeChange,
    disabled = false,
    variant = "dark",
    showTimeline = true,
    showUtilityControls = true,
  }) => (
    <section className={`space-y-2 rounded-md border p-3 ${variant === "light" ? "border-slate-200 bg-white" : "border-white/10 bg-black/20"}`}>
      <div className="flex items-center gap-2">
        <Button type="button" size="icon" onClick={onTogglePlay} disabled={disabled} aria-label={isPlaying ? "Pausar" : "Reproducir"} className={variant === "light" ? "h-12 w-12 rounded-full bg-teal-600 text-white hover:bg-teal-700" : ""}>
          {isPlaying ? <Pause /> : <Play />}
        </Button>
        <div className="min-w-0 flex-1 space-y-1">
          <Slider value={[progress]} max={100} step={0.1} disabled={disabled} onValueChange={([value]) => onSeek?.(((duration || 0) * value) / 100)} aria-label="Posicion de la cancion" />
          <div className={`flex justify-between text-[11px] font-mono ${variant === "light" ? "text-slate-500" : "text-gray-400"}`}>
            <span>{formatTime(currentTime)}</span>
            <span>{duration > 0 ? formatTime(duration) : "--:--"}</span>
          </div>
        </div>
        <Button
          type="button"
          variant={loopEnabled ? "default" : "outline"}
          size="icon"
          onClick={onToggleLoop}
          disabled={disabled}
          aria-label="Alternar loop"
          title="Loop"
          className={loopEnabled ? (variant === "light" ? "bg-teal-600 text-white hover:bg-teal-700" : "") : (variant === "light" ? "border-slate-200 bg-white text-slate-600 hover:bg-slate-50" : "border-white/10 text-gray-300")}
        >
          <Repeat />
        </Button>
      </div>
      {(showTimeline || showUtilityControls) && (
        <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-center">
          {showTimeline ? <ChordTimelineStrip chords={chords} activeIndex={currentIndex} onSeek={onSeek} compact disabled={disabled} variant={variant} /> : <div />}
          {showUtilityControls && (
            <label className={`flex items-center gap-2 text-xs ${variant === "light" ? "text-slate-600" : "text-gray-400"}`}>
              <Volume2 className="h-4 w-4" />
              <Slider className="w-20" value={[volume]} max={100} step={1} disabled={disabled} onValueChange={([value]) => onVolumeChange?.(value)} />
            </label>
          )}
          {showUtilityControls && (
            <label className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${variant === "light" ? "border-slate-200 text-slate-600" : "border-white/10 text-gray-300"}`}>
              <Gauge className="h-4 w-4" />
              <select
                value={playbackRate}
                disabled={disabled}
                onChange={(event) => onPlaybackRateChange?.(Number(event.target.value))}
                className={`bg-transparent outline-none ${variant === "light" ? "text-slate-700" : "text-gray-200"}`}
                aria-label="Velocidad"
              >
                {[0.5, 0.75, 1, 1.25, 1.5].map((rate) => <option key={rate} value={rate} className={variant === "light" ? "bg-white" : "bg-slate-900"}>{rate}x</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </section>
  )
);

PracticeSongPlayer.displayName = "PracticeSongPlayer";

export default PracticeSongPlayer;
